import { useState, useEffect, useMemo } from 'react';
import {
  FileText, Send, CheckCircle, Clock, XCircle, ArrowLeft, Camera, Upload,
  AlertCircle, FileUp, Activity, Plus, ShieldCheck, BookOpen, HeartPulse,
  Scale, Info, HelpCircle, AlertTriangle
} from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { api } from '../../api/client';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import './responsavel-mobile.css';
import { ROUTES } from '../../api/routes';
import { useResponsavelId } from './useResponsavelId';
import { useHojeReferencia } from '../../api/hooks/useSimClock';
import { ResponsavelTabBar } from '../../components/responsavel/ResponsavelTabBar';
import {
  filterJustificativasPorEscopo,
  resolverStatusExibicao,
  type EscopoJustificativa,
} from '../admin/justificativaEscopo';

interface Justificativa {
  id: string;
  dataFalta: string;
  motivo: string;
  status: string;
  criadoEm: string;
  aluno: { id: string; nome: string; matricula: string; turma?: { nome: string; serie: string } };
  revisadoPor?: { nome: string } | null;
  revisadoEm?: string | null;
  anexoUrl?: string | null;
  hasAnexo?: boolean;
}

function formatarDataAusencia(dataIsoOrYmd?: string | null): string {
  if (!dataIsoOrYmd) return 'Data não informada';
  const clean = dataIsoOrYmd.split('T')[0];
  const parts = clean.split('-');
  if (parts.length === 3) {
    const [y, m, d] = parts;
    return `${d}/${m}/${y}`;
  }
  const dt = new Date(dataIsoOrYmd);
  return isNaN(dt.getTime()) ? dataIsoOrYmd : dt.toLocaleDateString('pt-BR');
}

function parseMotivoResp(motivoStr: string) {
  const match = motivoStr.match(/^\[(.*?)\]\s*(.*)$/);
  if (match) {
    return { category: match[1], label: CATEGORIA_LABELS[match[1]] || match[1], text: match[2] };
  }
  return { category: 'OUTROS', label: 'Outros', text: motivoStr };
}

const CATEGORIA_LABELS: Record<string, string> = {
  MEDICO: 'Atestado Médico',
  ODONTO: 'Consulta Odontológica',
  LUTO: 'Luto Familiar',
  TRANSPORTE: 'Problema de Transporte',
  RELIGIOSO: 'Motivo Religioso',
  OUTROS: 'Outros Motivos',
};

const STATUS_BADGE: Record<string, { label: string; color: string; bg: string; icon: typeof CheckCircle }> = {
  ACEITA: { label: 'Abonada / Aprovada', color: '#16a34a', bg: '#dcfce7', icon: CheckCircle },
  ABONADA: { label: 'Abonada / Aprovada', color: '#16a34a', bg: '#dcfce7', icon: CheckCircle },
  PENDENTE: { label: 'Em Análise', color: '#d97706', bg: '#fef3c7', icon: Clock },
  REJEITADA: { label: 'Não Aceita', color: '#dc2626', bg: '#fee2e2', icon: XCircle },
  CANCELADA: { label: 'Cancelada', color: '#64748b', bg: '#f1f5f9', icon: XCircle },
};

const CATEGORIES = [
  { id: 'MEDICO', label: 'Médico', icon: HeartPulse, color: '#ef4444', bg: '#fef2f2' },
  { id: 'ODONTO', label: 'Odonto', icon: Activity, color: '#3b82f6', bg: '#eff6ff' },
  { id: 'LUTO', label: 'Luto', icon: AlertCircle, color: '#f59e0b', bg: '#fffbeb' },
  { id: 'OUTROS', label: 'Outros', icon: Clock, color: '#8b5cf6', bg: '#f5f3ff' },
];

function useIsMobile(bp = 768) {
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' ? window.innerWidth < bp : false);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < bp);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [bp]);
  return isMobile;
}

export function Justificativas() {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const responsavelId = useResponsavelId();
  const { hojeStr } = useHojeReferencia();

  const [justificativas, setJustificativas] = useState<Justificativa[]>([]);
  const [filhos, setFilhos] = useState<{ vinculoId: string; aluno: { id: string; nome: string } }[]>([]);
  const [loading, setLoading] = useState(true);
  const [escopo, setEscopo] = useState<EscopoJustificativa>('ATIVAS');

  // Wizard State
  const [showWizard, setShowWizard] = useState(false);
  const [step, setStep] = useState(1);
  const [formAlunoId, setFormAlunoId] = useState('');
  const [formDataInicio, setFormDataInicio] = useState('');
  const [formDataFim, setFormDataFim] = useState('');
  const [formCategory, setFormCategory] = useState('MEDICO');
  const [formMotivo, setFormMotivo] = useState('');
  
  // File Upload State
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [hasAttachment, setHasAttachment] = useState(false);
  
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!responsavelId) return;
    Promise.all([
      api.get(ROUTES.mobileResponsavel.justificativas(responsavelId)).catch(() => ({ data: [] })),
      api.get(ROUTES.mobileResponsavel.resumo(responsavelId)).catch(() => ({ data: { alunos: [] } })),
    ]).then(([justRes, resumoRes]) => {
      setJustificativas(justRes.data || []);
      const alunos = (resumoRes.data?.alunos || []);
      setFilhos(alunos);
      if (alunos.length > 0 && !formAlunoId) setFormAlunoId(alunos[0].aluno.id);
    }).finally(() => setLoading(false));
  }, [responsavelId]);

  const openWizard = () => {
    setStep(1);
    setFormDataInicio(new Date().toISOString().split('T')[0]);
    setFormDataFim('');
    setFormCategory('MEDICO');
    setFormMotivo('');
    setSelectedFile(null);
    setPreviewUrl(null);
    setHasAttachment(false);
    setShowWizard(true);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setSelectedFile(file);
      setHasAttachment(true);
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    }
  };

  const handleSendJustificativa = async () => {
    if (sending) return;
    if (!formAlunoId || !formDataInicio || !formMotivo) {
      toast.warning('Preencha os campos obrigatórios');
      return;
    }
    setSending(true);
    try {
      let anexoBase64 = undefined;
      if (selectedFile) {
        anexoBase64 = await new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.readAsDataURL(selectedFile);
        });
      }

      const finalMotivo = `[${formCategory}] ${formMotivo}`;
      const res = await api.post(ROUTES.mobileResponsavel.justificativas(responsavelId), {
        alunoId: formAlunoId,
        dataFalta: formDataInicio,
        dataFim: formDataFim && formDataFim !== formDataInicio ? formDataFim : undefined,
        motivo: finalMotivo,
        anexoBase64,
      });

      const created = Array.isArray(res.data) ? res.data : [res.data];
      const mapped = created.map((item: Justificativa) => ({
        ...item,
        status: item.status || 'PENDENTE',
        hasAnexo: Boolean(item.anexoUrl) || hasAttachment,
        motivo: item.motivo || finalMotivo,
      }));
      setJustificativas((prev) => {
        const combined = [...mapped, ...prev];
        const seen = new Set<string>();
        return combined.filter((item) => {
          if (!item.id || seen.has(item.id)) return false;
          seen.add(item.id);
          return true;
        });
      });
      setShowWizard(false);
      toast.success('Justificativa enviada com sucesso!');
    } catch {
      toast.error('Erro ao enviar justificativa');
    } finally {
      setSending(false);
    }
  };

  const handleFileChange = handleFileSelect;
  const removeFile = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setHasAttachment(false);
  };
  const enviar = handleSendJustificativa;
  const nextStep = () => {
    if (typeof navigator !== 'undefined' && 'vibrate' in navigator) navigator.vibrate(10);
    setStep(s => s + 1);
  };
  const prevStep = () => {
    if (typeof navigator !== 'undefined' && 'vibrate' in navigator) navigator.vibrate(10);
    setStep(s => s - 1);
  };

  const listaEscopo = useMemo(
    () => filterJustificativasPorEscopo(justificativas, escopo, hojeStr),
    [justificativas, escopo, hojeStr],
  );
  const ativasCount = filterJustificativasPorEscopo(justificativas, 'ATIVAS', hojeStr).length;
  const historicoCount = filterJustificativasPorEscopo(justificativas, 'HISTORICO', hojeStr).length;

  // ═══ DESKTOP VIEW ═══
  if (!isMobile) {
    const emAnaliseCount = justificativas.filter(j => j.status === 'PENDENTE').length;
    const aceitasCount = justificativas.filter(j => j.status === 'ACEITA').length;

    return (
      <div className="admin-page" style={{ maxWidth: 1400, width: '100%', margin: '0 auto', paddingBottom: 32 }}>
        {/* ═══ Standardized Full-Width Hero Banner ═══ */}
        <div style={{
          background: 'radial-gradient(135% 140% at 100% 0%, #312e81 0%, #1e1b4b 55%, #0f172a 100%)',
          borderRadius: 20,
          padding: '20px 28px',
          color: '#ffffff',
          boxShadow: '0 8px 24px rgba(15, 23, 42, 0.14)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 16,
          marginBottom: 20,
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div style={{ minWidth: 260, position: 'relative', zIndex: 2 }}>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(8px)',
              padding: '3px 10px', borderRadius: 20, fontSize: '0.7rem', fontWeight: 600,
              color: '#e0e7ff', marginBottom: 6,
            }}>
              <FileText size={12} color="#818cf8" /> Protocolo Digital de Ausências & Atestados
            </div>
            <h1 style={{ fontSize: '1.45rem', fontWeight: 800, margin: 0, color: '#ffffff', letterSpacing: '-0.02em' }}>
              Justificativas de Falta & Atestados
            </h1>
            <p style={{ margin: '4px 0 0', fontSize: '0.8rem', color: 'rgba(255,255,255,0.72)' }}>
              Envio digital com análise oficial pela coordenação escolar em conformidade com a LDB/MEC
            </p>
          </div>

          {/* Hero KPIs */}
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', position: 'relative', zIndex: 2 }}>
            <div style={{
              background: 'rgba(255,255,255,0.08)', backdropFilter: 'blur(8px)',
              border: '1px solid rgba(255,255,255,0.12)', borderRadius: 14,
              padding: '8px 16px', textAlign: 'center', minWidth: 85,
            }}>
              <div style={{ fontSize: '1.2rem', fontWeight: 800, color: '#38bdf8', lineHeight: 1.1 }}>{justificativas.length}</div>
              <div style={{ fontSize: '0.62rem', fontWeight: 700, color: 'rgba(255,255,255,0.65)', textTransform: 'uppercase', marginTop: 3 }}>Total</div>
            </div>
            <div style={{
              background: 'rgba(255,255,255,0.08)', backdropFilter: 'blur(8px)',
              border: '1px solid rgba(255,255,255,0.12)', borderRadius: 14,
              padding: '8px 16px', textAlign: 'center', minWidth: 85,
            }}>
              <div style={{ fontSize: '1.2rem', fontWeight: 800, color: '#fbbf24', lineHeight: 1.1 }}>{emAnaliseCount}</div>
              <div style={{ fontSize: '0.62rem', fontWeight: 700, color: 'rgba(255,255,255,0.65)', textTransform: 'uppercase', marginTop: 3 }}>Em Análise</div>
            </div>
            <div style={{
              background: 'rgba(255,255,255,0.08)', backdropFilter: 'blur(8px)',
              border: '1px solid rgba(255,255,255,0.12)', borderRadius: 14,
              padding: '8px 16px', textAlign: 'center', minWidth: 85,
            }}>
              <div style={{ fontSize: '1.2rem', fontWeight: 800, color: '#4ade80', lineHeight: 1.1 }}>{aceitasCount}</div>
              <div style={{ fontSize: '0.62rem', fontWeight: 700, color: 'rgba(255,255,255,0.65)', textTransform: 'uppercase', marginTop: 3 }}>Abonadas</div>
            </div>
          </div>
        </div>

        {/* ═══ 2-COLUMN BALANCED DESKTOP SPLIT LAYOUT ═══ */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1.22fr) minmax(0, 0.78fr)',
          gap: 20,
          alignItems: 'start',
          width: '100%',
        }}>
          {/* ─── Coluna Esquerda: Protocolos e Histórico ─── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Toolbar com Scope Tabs & Action */}
            <div style={{
              background: '#ffffff',
              borderRadius: 16,
              padding: '12px 18px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
              border: '1px solid #e2e8f0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 12
            }}>
              {/* Scope Tabs */}
              <div style={{ display: 'flex', gap: 6 }}>
                {[
                  { key: 'ATIVAS' as const, label: `Ativas (${ativasCount})` },
                  { key: 'HISTORICO' as const, label: `Histórico (${historicoCount})` },
                ].map((opt) => (
                  <button
                    key={opt.key}
                    onClick={() => setEscopo(opt.key)}
                    style={{
                      padding: '7px 14px', borderRadius: 8, border: '1px solid',
                      borderColor: escopo === opt.key ? '#2563eb' : '#e2e8f0',
                      background: escopo === opt.key ? '#eff6ff' : '#ffffff',
                      color: escopo === opt.key ? '#2563eb' : '#475569',
                      fontWeight: escopo === opt.key ? 700 : 500, fontSize: '0.78rem',
                      cursor: 'pointer', transition: 'all 0.15s',
                    }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>

              <button
                onClick={openWizard}
                style={{
                  background: '#2563eb', color: '#ffffff', border: 'none', borderRadius: 8,
                  padding: '8px 16px', fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 6, boxShadow: '0 2px 6px rgba(37,99,235,0.25)',
                }}
              >
                <Plus size={15} /> Nova Justificativa
              </button>
            </div>

            {/* Lista de Justificativas */}
            {loading ? (
              <div style={{ textAlign: 'center', padding: 48, color: '#94a3b8' }}>Carregando justificativas...</div>
            ) : listaEscopo.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 48, background: '#fff', borderRadius: 20, border: '1px solid #e2e8f0' }}>
                <FileText size={40} style={{ color: '#cbd5e1', margin: '0 auto 12px' }} />
                <h3 style={{ margin: '0 0 6px', color: '#0f172a', fontSize: '1rem', fontWeight: 700 }}>Nenhuma justificativa neste filtro</h3>
                <p style={{ color: '#64748b', fontSize: '0.82rem', margin: 0 }}>Clique em "Nova Justificativa" para protocolar um atestado médico.</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {listaEscopo.map((j) => {
                  const statusExib = resolverStatusExibicao(j, hojeStr);
                  const badge = STATUS_BADGE[statusExib] || { label: statusExib, color: '#64748b', bg: '#f1f5f9', icon: Clock };
                  const parsed = parseMotivoResp(j.motivo);
                  const IconStatus = badge.icon;

                  return (
                    <div
                      key={j.id}
                      style={{
                        background: '#ffffff', borderRadius: 20, border: '1px solid #e2e8f0', padding: 20,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.03)', display: 'flex', flexDirection: 'column', gap: 12,
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: '0.74rem', color: '#64748b', fontWeight: 600 }}>
                          Data da Ausência: <strong style={{ color: '#0f172a' }}>{formatarDataAusencia(j.dataFalta)}</strong>
                        </span>
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: 5,
                          fontSize: '0.72rem', fontWeight: 800, color: badge.color, background: badge.bg,
                          padding: '4px 10px', borderRadius: 8,
                        }}>
                          <IconStatus size={14} /> {badge.label}
                        </span>
                      </div>

                      <div>
                        <h3 style={{ margin: 0, fontSize: '1.02rem', fontWeight: 800, color: '#0f172a' }}>
                          {j.aluno?.nome || 'Aluno'}
                        </h3>
                        <div style={{ fontSize: '0.76rem', color: '#64748b', marginTop: 2 }}>
                          Categoria: <strong style={{ color: '#2563eb' }}>{parsed.label}</strong>
                          {j.aluno?.turma?.nome ? ` • Turma: ${j.aluno.turma.nome}` : ''}
                        </div>
                      </div>

                      <div style={{ background: '#f8fafc', borderRadius: 12, padding: 12, border: '1px solid #f1f5f9', fontSize: '0.82rem', color: '#334155', lineHeight: 1.45 }}>
                        {parsed.text}
                      </div>

                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.74rem', color: '#94a3b8', marginTop: 'auto', paddingTop: 4 }}>
                        <span>Protocolado em: {new Date(j.criadoEm).toLocaleDateString('pt-BR')}</span>
                        {(j.anexoUrl || j.hasAnexo) && (
                          <span style={{ color: '#2563eb', fontWeight: 800, background: '#eff6ff', padding: '2px 8px', borderRadius: 6 }}>
                            📎 Comprovante Anexado
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* ─── Coluna Direita: Guia Oficial de Justificativas & Legislação MEC ─── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Card: Casos Amparados por Lei (MEC / LDB) */}
            <div style={{
              background: '#ffffff',
              borderRadius: 20,
              border: '1px solid #e2e8f0',
              padding: 22,
              boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
              display: 'flex',
              flexDirection: 'column',
              gap: 14
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#0f172a', fontWeight: 800, fontSize: '0.94rem' }}>
                <Scale size={18} color="#2563eb" /> Guia Oficial: Casos Amparados (MEC/LDB)
              </div>
              <p style={{ fontSize: '0.76rem', color: '#64748b', margin: 0, lineHeight: 1.4 }}>
                Confira as situações e amparos legais previstos na legislação educacional para justificativa e abono de faltas:
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                  <div style={{ width: 28, height: 28, borderRadius: 8, background: '#eff6ff', color: '#2563eb', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 2 }}>
                    <HeartPulse size={15} />
                  </div>
                  <div>
                    <strong style={{ fontSize: '0.8rem', color: '#0f172a', display: 'block' }}>1. Saúde & Atestado Médico/Odontológico</strong>
                    <span style={{ fontSize: '0.72rem', color: '#64748b', lineHeight: 1.35, display: 'block' }}>
                      Doenças, consultas, cirurgias ou internações amparadas pelo <em>Decreto-Lei nº 1.044/69</em>. Atestado deve conter CRM/CRO.
                    </span>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                  <div style={{ width: 28, height: 28, borderRadius: 8, background: '#fdf2f8', color: '#db2777', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 2 }}>
                    <AlertCircle size={15} />
                  </div>
                  <div>
                    <strong style={{ fontSize: '0.8rem', color: '#0f172a', display: 'block' }}>2. Luto e Falecimento Familiar</strong>
                    <span style={{ fontSize: '0.72rem', color: '#64748b', lineHeight: 1.35, display: 'block' }}>
                      Licença por nojo para familiares diretos (pais, irmãos, avós), mediante certidão de óbito.
                    </span>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                  <div style={{ width: 28, height: 28, borderRadius: 8, background: '#f0fdf4', color: '#16a34a', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 2 }}>
                    <ShieldCheck size={15} />
                  </div>
                  <div>
                    <strong style={{ fontSize: '0.8rem', color: '#0f172a', display: 'block' }}>3. Liberdade de Consciência e Crença</strong>
                    <span style={{ fontSize: '0.72rem', color: '#64748b', lineHeight: 1.35, display: 'block' }}>
                      <em>Lei Federal nº 13.796/2019</em> garante prestação alternativa de provas para alunos que guardam dias religiosos.
                    </span>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                  <div style={{ width: 28, height: 28, borderRadius: 8, background: '#fef3c7', color: '#d97706', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 2 }}>
                    <Scale size={15} />
                  </div>
                  <div>
                    <strong style={{ fontSize: '0.8rem', color: '#0f172a', display: 'block' }}>4. Convocação Judicial ou Oficial</strong>
                    <span style={{ fontSize: '0.72rem', color: '#64748b', lineHeight: 1.35, display: 'block' }}>
                      Comparecimento obrigatório em audiências judiciais, órgãos públicos ou alistamento.
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Card: Prazos e Exigência LDB 75% */}
            <div style={{
              background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
              borderRadius: 20,
              border: '1px solid #e2e8f0',
              padding: 20,
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#0f172a', fontWeight: 800, fontSize: '0.86rem' }}>
                <BookOpen size={16} color="#2563eb" /> Prazos e Assiduidade Escolar
              </div>
              
              <div style={{ fontSize: '0.74rem', color: '#475569', lineHeight: 1.45, display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div>
                  ⏱️ <strong>Prazo Regulamentar:</strong> Protocole o atestado em até <strong>72 horas úteis</strong> após a emissão médica ou retorno do aluno.
                </div>
                <div>
                  🎯 <strong>Meta LDB (Art. 24, VI):</strong> A Lei de Diretrizes e Bases exige o mínimo de <strong>75% de frequência</strong> para aprovação direta.
                </div>
                <div>
                  📝 <strong>Reposição de Atividades:</strong> O atestado deferido assegura ao aluno o direito de realizar 2ª chamada de avaliações e tarefas.
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Modal de Criação Desktop */}
        {showWizard && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 20, zIndex: 9999,
          }}>
            <div style={{
              background: '#ffffff', borderRadius: 20, width: '100%', maxWidth: 540,
              padding: 24, boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1)',
              display: 'flex', flexDirection: 'column', gap: 18, border: '1px solid #e2e8f0',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 700, color: '#0f172a' }}>
                  Nova Justificativa de Ausência
                </h3>
                <button
                  onClick={() => setShowWizard(false)}
                  style={{
                    background: '#f1f5f9', border: 'none', width: 32, height: 32,
                    borderRadius: '50%', cursor: 'pointer', color: '#64748b',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >
                  ✕
                </button>
              </div>

              {/* Aluno Selection */}
              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, color: '#334155', marginBottom: 6 }}>
                  Aluno *
                </label>
                <select
                  value={formAlunoId}
                  onChange={(e) => setFormAlunoId(e.target.value)}
                  style={{
                    width: '100%', padding: '10px 14px', borderRadius: 10,
                    border: '1px solid #cbd5e1', background: '#ffffff', color: '#0f172a',
                    colorScheme: 'light', fontSize: '0.875rem', outline: 'none',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.04)', boxSizing: 'border-box',
                  }}
                >
                  {filhos.map((f) => (
                    <option key={f.aluno.id} value={f.aluno.id}>{f.aluno.nome}</option>
                  ))}
                </select>
              </div>

              {/* Data Range */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, color: '#334155', marginBottom: 6 }}>
                    Data Início *
                  </label>
                  <input
                    type="date"
                    value={formDataInicio}
                    onChange={(e) => setFormDataInicio(e.target.value)}
                    style={{
                      width: '100%', padding: '9px 12px', borderRadius: 10,
                      border: '1px solid #cbd5e1', background: '#ffffff', color: '#0f172a',
                      colorScheme: 'light', fontSize: '0.875rem', outline: 'none',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.04)', boxSizing: 'border-box',
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, color: '#334155', marginBottom: 6 }}>
                    Data Fim (Opcional)
                  </label>
                  <input
                    type="date"
                    value={formDataFim}
                    onChange={(e) => setFormDataFim(e.target.value)}
                    style={{
                      width: '100%', padding: '9px 12px', borderRadius: 10,
                      border: '1px solid #cbd5e1', background: '#ffffff', color: '#0f172a',
                      colorScheme: 'light', fontSize: '0.875rem', outline: 'none',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.04)', boxSizing: 'border-box',
                    }}
                  />
                </div>
              </div>

              {/* Categoria */}
              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, color: '#334155', marginBottom: 6 }}>
                  Categoria do Motivo
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
                  {CATEGORIES.map((cat) => (
                    <button
                      key={cat.id}
                      type="button"
                      onClick={() => setFormCategory(cat.id)}
                      style={{
                        padding: '10px 8px', borderRadius: 10, border: '1px solid',
                        borderColor: formCategory === cat.id ? '#2563eb' : '#cbd5e1',
                        background: formCategory === cat.id ? '#eff6ff' : '#ffffff',
                        color: formCategory === cat.id ? '#2563eb' : '#475569',
                        fontSize: '0.78rem', fontWeight: formCategory === cat.id ? 700 : 500,
                        cursor: 'pointer', textAlign: 'center', transition: 'all 0.15s',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                      }}
                    >
                      {cat.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Motivo */}
              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, color: '#334155', marginBottom: 6 }}>
                  Descrição / Motivo da Falta *
                </label>
                <textarea
                  rows={3}
                  value={formMotivo}
                  onChange={(e) => setFormMotivo(e.target.value)}
                  placeholder="Explique detalhadamente o motivo da ausência..."
                  style={{
                    width: '100%', padding: '10px 14px', borderRadius: 10,
                    border: '1px solid #cbd5e1', background: '#ffffff', color: '#0f172a',
                    colorScheme: 'light', fontSize: '0.875rem', outline: 'none', resize: 'vertical',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.04)', boxSizing: 'border-box',
                  }}
                />
              </div>

              {/* Upload Atestado */}
              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, color: '#334155', marginBottom: 6 }}>
                  Atestado Médico / Documento Comprobatório (Opcional)
                </label>
                {!hasAttachment ? (
                  <label style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                    padding: '14px 16px', borderRadius: 10, border: '2px dashed #cbd5e1',
                    background: '#f8fafc', color: '#475569', cursor: 'pointer',
                    fontSize: '0.84rem', fontWeight: 600, transition: 'all 0.15s',
                  }}>
                    <Upload size={18} color="#2563eb" />
                    <span>Clique para anexar foto ou PDF do atestado</span>
                    <input
                      type="file"
                      accept="image/*,.pdf"
                      onChange={handleFileSelect}
                      style={{ display: 'none' }}
                    />
                  </label>
                ) : (
                  <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '10px 14px', borderRadius: 10, border: '1px solid #bbf7d0',
                    background: '#f0fdf4', color: '#166534', fontSize: '0.82rem', fontWeight: 600,
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <CheckCircle size={18} color="#16a34a" />
                      <span>{selectedFile?.name || 'Atestado selecionado com sucesso'}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => { setSelectedFile(null); setPreviewUrl(null); setHasAttachment(false); }}
                      style={{ background: 'transparent', border: 'none', color: '#ef4444', fontWeight: 700, cursor: 'pointer', fontSize: '0.78rem' }}
                    >
                      Remover
                    </button>
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 6, paddingTop: 14, borderTop: '1px solid #f1f5f9' }}>
                <button
                  type="button"
                  onClick={() => setShowWizard(false)}
                  style={{
                    background: '#f8fafc', border: '1px solid #e2e8f0', padding: '10px 18px',
                    borderRadius: 10, fontSize: '0.84rem', fontWeight: 600, color: '#475569', cursor: 'pointer',
                  }}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  disabled={sending}
                  onClick={handleSendJustificativa}
                  style={{
                    background: '#2563eb', border: 'none', padding: '10px 22px',
                    borderRadius: 10, fontSize: '0.84rem', fontWeight: 700, color: '#ffffff', cursor: 'pointer',
                    boxShadow: '0 4px 12px rgba(37,99,235,0.25)',
                  }}
                >
                  {sending ? 'Enviando...' : 'Protocolar Justificativa'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="resp-mobile-root">
      {/* ═══ NAVY HEADER ═══ */}
      <div className="resp-navy-header" style={{ padding: 'max(env(safe-area-inset-top, 40px), 40px) 20px 30px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8, position: 'relative', zIndex: 10 }}>
          <button 
            onClick={() => navigate('/responsavel/dashboard')} 
            style={{ 
              background: 'transparent', border: 'none', color: 'white', 
              padding: '10px', marginLeft: '-10px', 
              display: 'flex', cursor: 'pointer' 
            }}
          >
            <ArrowLeft size={24} />
          </button>
          <div>
            <h1 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600, color: 'white', letterSpacing: '-0.3px' }}>
              Justificativas
            </h1>
            <p style={{ margin: '4px 0 0', fontSize: '0.75rem', color: 'rgba(255,255,255,0.7)', fontWeight: 400 }}>
              Ativas, histórico e envio de atestados
            </p>
          </div>
        </div>
      </div>

      {/* ═══ CONTENT AREA ═══ */}
      <div className="resp-content-area" style={{ paddingBottom: 100 }}>
        <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
          {(
            [
              { key: 'ATIVAS' as const, label: `Ativas (${ativasCount})` },
              { key: 'HISTORICO' as const, label: `Histórico (${historicoCount})` },
            ] as const
          ).map((opt) => (
            <button
              key={opt.key}
              type="button"
              onClick={() => setEscopo(opt.key)}
              style={{
                padding: '8px 12px',
                borderRadius: 999,
                border: escopo === opt.key ? 'none' : '1px solid #e2e8f0',
                background: escopo === opt.key ? '#0f172a' : '#fff',
                color: escopo === opt.key ? '#fff' : '#475569',
                fontSize: '0.72rem',
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
        
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8', fontSize: '0.85rem' }}>Carregando histórico...</div>
        ) : listaEscopo.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, background: 'white', borderRadius: 16, boxShadow: '0 1px 8px rgba(0,0,0,0.04)' }}>
            <FileText size={32} style={{ color: '#cbd5e1', margin: '0 auto 12px' }} />
            <p style={{ color: '#64748b', fontSize: '0.85rem', margin: 0, fontWeight: 500 }}>
              {justificativas.length === 0
                ? 'Nenhuma justificativa enviada ainda.'
                : escopo === 'ATIVAS'
                  ? 'Nenhuma justificativa ativa. Veja o histórico.'
                  : 'Nenhum registro neste filtro.'}
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {listaEscopo.map((j) => {
              const statusUi = resolverStatusExibicao(j, hojeStr);
              const st = STATUS_BADGE[statusUi] || STATUS_BADGE.PENDENTE;
              const Icon = st.icon;
              const parsed = parseMotivoResp(j.motivo || '');
              const temAnexo = Boolean(j.anexoUrl) || Boolean(j.hasAnexo);
              const dataFaltaLabel = (() => {
                if (!j.dataFalta) return 'Data não informada';
                try { 
                  const dtStr = j.dataFalta.includes('T') ? j.dataFalta : `${j.dataFalta}T12:00:00`;
                  const d = new Date(dtStr);
                  if (isNaN(d.getTime())) return j.dataFalta;
                  return d.toLocaleDateString('pt-BR'); 
                } catch { 
                  return j.dataFalta; 
                }
              })();
              
              return (
                <div key={j.id} style={{
                  background: 'white', borderRadius: 16, padding: '16px',
                  boxShadow: '0 2px 10px rgba(0,0,0,0.04)',
                  border: `1px solid ${
                    statusUi === 'PENDENTE' ? '#fef3c7'
                      : statusUi === 'VENCIDA' ? '#e2e8f0'
                        : statusUi === 'ACEITA' ? '#bbf7d0'
                          : statusUi === 'RECUSADA' ? '#fecaca'
                            : '#f1f5f9'
                  }`,
                  position: 'relative', overflow: 'hidden',
                  opacity: statusUi === 'VENCIDA' ? 0.92 : 1,
                }}>
                  <div style={{ position: 'absolute', top: 0, left: 0, bottom: 0, width: 4, background: st.color }}></div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10, paddingLeft: 8 }}>
                    <span style={{ 
                      display: 'flex', alignItems: 'center', gap: 4,
                      fontSize: '0.65rem', fontWeight: 700, color: st.color, background: st.bg, 
                      padding: '4px 10px', borderRadius: 8, letterSpacing: '0.5px' 
                    }}>
                      <Icon size={12} /> {st.label}
                    </span>
                    <span style={{ fontSize: '0.7rem', color: '#94a3b8', fontWeight: 500 }}>
                      Falta em: {dataFaltaLabel}
                    </span>
                  </div>
                  <h3 style={{ margin: '0 0 2px 8px', fontSize: '0.95rem', fontWeight: 600, color: '#0f172a' }}>
                    {j.aluno?.nome || 'Aluno'}
                  </h3>
                  <div style={{ margin: '0 0 6px 8px', fontSize: '0.68rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>
                    {parsed.label}
                    {j.aluno?.turma?.nome ? ` · ${j.aluno.turma.nome}` : ''}
                  </div>
                  <p style={{ margin: '0 0 10px 8px', fontSize: '0.8rem', color: '#64748b', lineHeight: 1.4 }}>
                    {parsed.text}
                  </p>

                  {statusUi === 'VENCIDA' && (
                    <p style={{ margin: '0 0 8px 8px', fontSize: '0.72rem', color: '#64748b', fontWeight: 600 }}>
                      Prazo da falta passou sem decisão da escola
                    </p>
                  )}
                  {statusUi === 'ACEITA' && (
                    <p style={{ margin: '0 0 8px 8px', fontSize: '0.72rem', color: '#166534', fontWeight: 600 }}>
                      Ausência abonada pela escola
                      {j.revisadoPor?.nome ? ` · ${j.revisadoPor.nome}` : ''}
                    </p>
                  )}
                  {statusUi === 'RECUSADA' && (
                    <p style={{ margin: '0 0 8px 8px', fontSize: '0.72rem', color: '#b91c1c', fontWeight: 600 }}>
                      Recusada pela escola
                      {j.revisadoPor?.nome ? ` · ${j.revisadoPor.nome}` : ''}
                    </p>
                  )}
                  
                  {temAnexo && (
                    <div style={{ marginLeft: 8, display: 'inline-flex', alignItems: 'center', gap: 6, background: '#f8fafc', padding: '4px 10px', borderRadius: 6, border: '1px solid #e2e8f0', fontSize: '0.7rem', color: '#475569', fontWeight: 600 }}>
                      <FileUp size={12} style={{ color: '#3b82f6' }} /> Atestado Anexado
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ═══ FLOATING ACTION BUTTON ═══ */}
      {!showWizard && (
        <button
          type="button"
          onClick={openWizard}
          style={{
            position: 'fixed', bottom: 'max(96px, calc(env(safe-area-inset-bottom, 16px) + 80px))', right: 20, width: 56, height: 56, borderRadius: 28,
            background: 'linear-gradient(135deg, #2563eb, #1d4ed8)', color: 'white',
            display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none',
            boxShadow: '0 4px 20px rgba(37,99,235,0.4)', zIndex: 90, cursor: 'pointer',
          }}
        >
          <Plus size={26} />
        </button>
      )}

      {/* ═══ WIZARD TELA CHEIA ═══ */}
      {showWizard && (
        <div className="resp-justif-overlay" role="dialog" aria-modal="true" aria-label="Nova justificativa">
          <div className="resp-justif-wizard">
            <header className="resp-justif-wizard-head">
              <div>
                <h3>Nova Justificativa</h3>
                <p>Etapa {step} de 3</p>
              </div>
              <button
                type="button"
                className="resp-justif-wizard-close"
                onClick={() => setShowWizard(false)}
                aria-label="Fechar"
              >
                <XCircle size={20} />
              </button>
            </header>

            <div className="resp-justif-steps" aria-hidden>
              {[1, 2, 3].map((i) => (
                <span key={i} className={i <= step ? 'is-on' : ''} />
              ))}
            </div>

            <div className="resp-justif-wizard-body">
              {step === 1 && (
                <>
                  <div>
                    <label className="resp-justif-label">Qual filho(a) faltou?</label>
                    <div className="resp-justif-filhos">
                      {filhos.map((f) => (
                        <button
                          type="button"
                          key={f.aluno.id}
                          className={`resp-justif-filho${formAlunoId === f.aluno.id ? ' is-on' : ''}`}
                          onClick={() => setFormAlunoId(f.aluno.id)}
                        >
                          {f.aluno.nome}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="resp-justif-label">Período da ausência</label>
                    <div className="resp-justif-dates">
                      <div>
                        <label className="resp-justif-field-label">Data inicial (obrigatório)</label>
                        <input
                          type="date"
                          className="resp-justif-date"
                          value={formDataInicio}
                          onChange={(e) => setFormDataInicio(e.target.value)}
                        />
                      </div>
                      <div>
                        <label className="resp-justif-field-label">Data final (opcional)</label>
                        <input
                          type="date"
                          className="resp-justif-date"
                          value={formDataFim}
                          min={formDataInicio}
                          onChange={(e) => setFormDataFim(e.target.value)}
                        />
                      </div>
                    </div>
                    <small style={{ display: 'block', marginTop: 6, fontSize: '0.72rem', color: '#64748b' }}>
                      💡 Para ausência de apenas 1 dia, deixe a Data Final vazia. Preencha apenas se for um atestado de vários dias consecutivos.
                    </small>
                  </div>
                </>
              )}

              {step === 2 && (
                <>
                  <div>
                    <label className="resp-justif-label">Motivo principal</label>
                    <div className="resp-justif-cats">
                      {CATEGORIES.map((cat) => (
                        <button
                          type="button"
                          key={cat.id}
                          className="resp-justif-cat"
                          onClick={() => setFormCategory(cat.id)}
                          style={{
                            borderColor: formCategory === cat.id ? cat.color : undefined,
                            background: formCategory === cat.id ? cat.bg : undefined,
                          }}
                        >
                          <cat.icon
                            size={22}
                            style={{ color: formCategory === cat.id ? cat.color : '#94a3b8' }}
                          />
                          <span style={{ color: formCategory === cat.id ? cat.color : undefined }}>
                            {cat.label}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="resp-justif-label">Observações (opcional)</label>
                    <textarea
                      className="resp-justif-textarea"
                      value={formMotivo}
                      onChange={(e) => setFormMotivo(e.target.value)}
                      placeholder="Detalhes adicionais..."
                      rows={2}
                    />
                  </div>
                </>
              )}

              {step === 3 && (
                <>
                  <div>
                    <label className="resp-justif-label">Anexar atestado (opcional)</label>
                    {!hasAttachment ? (
                      <div className="resp-justif-attach-grid">
                        <input
                          type="file"
                          id="atestado-camera"
                          accept="image/*"
                          capture="environment"
                          style={{ display: 'none' }}
                          onChange={handleFileChange}
                        />
                        <label htmlFor="atestado-camera" className="resp-justif-attach">
                          <Camera size={26} style={{ color: '#3b82f6' }} />
                          <strong>Tirar foto</strong>
                          <small>Câmera</small>
                        </label>
                        <input
                          type="file"
                          id="atestado-arquivo"
                          accept="image/*,application/pdf"
                          style={{ display: 'none' }}
                          onChange={handleFileChange}
                        />
                        <label htmlFor="atestado-arquivo" className="resp-justif-attach">
                          <Upload size={26} style={{ color: '#8b5cf6' }} />
                          <strong>Arquivo</strong>
                          <small>PDF ou galeria</small>
                        </label>
                      </div>
                    ) : (
                      <div
                        style={{
                          border: '2px solid #10b981',
                          borderRadius: 12,
                          padding: 12,
                          background: '#ecfdf5',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 8,
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                          {previewUrl ? (
                            <div
                              style={{
                                width: 40,
                                height: 40,
                                borderRadius: 8,
                                backgroundImage: `url(${previewUrl})`,
                                backgroundSize: 'cover',
                                flexShrink: 0,
                              }}
                            />
                          ) : (
                            <FileText size={22} color="#059669" />
                          )}
                          <span
                            style={{
                              fontSize: '0.8rem',
                              fontWeight: 700,
                              color: '#065f46',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {selectedFile?.name || 'Atestado anexado'}
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={removeFile}
                          style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer' }}
                        >
                          <XCircle size={22} />
                        </button>
                      </div>
                    )}
                  </div>
                  <p className="resp-justif-tip">
                    <AlertCircle size={14} style={{ verticalAlign: -2, marginRight: 4 }} />
                    A escola analisa o pedido. Se aprovar, a falta é abonada e você recebe aviso no app.
                  </p>
                </>
              )}
            </div>

            <footer className="resp-justif-wizard-foot">
              {step > 1 && (
                <button type="button" className="is-back" onClick={prevStep}>
                  Voltar
                </button>
              )}
              {step < 3 ? (
                <button
                  type="button"
                  className="is-next"
                  onClick={nextStep}
                  disabled={
                    (step === 1 && (!formAlunoId || !formDataInicio)) ||
                    (step === 2 && !formCategory)
                  }
                >
                  Continuar
                </button>
              ) : (
                <button type="button" className="is-send" onClick={enviar} disabled={sending}>
                  <Send size={16} />
                  {sending ? 'Enviando…' : 'Enviar'}
                </button>
              )}
            </footer>
          </div>
        </div>
      )}

      {/* ═══ BARRA INFERIOR DE NAVEGAÇÃO MOBILE ═══ */}
      {!showWizard && <ResponsavelTabBar activeTab="justif" />}
    </div>
  );
}
